import { C } from "@hikari/vault-core";
import { useState } from "react";
import Section from "../components/Section";

/* ------------------------------------------------------------------ */
/*  Hero                                                              */
/* ------------------------------------------------------------------ */
function Hero({ onEnter }) {
    return (
        <section
            style={{
                padding: "6rem 1.5rem 5rem",
                textAlign: "center",
                background:
                    "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(11,126,163,0.18), transparent 60%)",
            }}
        >
            <div
                style={{
                    fontSize: "0.72rem",
                    letterSpacing: "0.3em",
                    color: C.accent,
                    marginBottom: "1.25rem",
                    fontFamily: C.font,
                }}
            >
                HIKARI BRAND · SECURITY &amp; PRIVACY
            </div>
            <h1
                style={{
                    fontSize: "clamp(2.2rem, 6vw, 4rem)",
                    fontWeight: 500,
                    margin: "0 auto 1.5rem",
                    maxWidth: "20ch",
                    lineHeight: 1.05,
                    fontFamily: C.font,
                    color: C.text,
                }}
            >
                The vault for everything you'd otherwise screenshot.
            </h1>
            <p
                style={{
                    color: C.muted,
                    maxWidth: "56ch",
                    margin: "0 auto 2.5rem",
                    lineHeight: 1.65,
                    fontFamily: C.font,
                    fontSize: "1rem",
                }}
            >
                Passport. Seed phrase. 2FA codes. Will. Wi-Fi password. Things
                you'd never put in Notes.{" "}
                <span style={{ color: C.text }}>
                    No account. No email. No trace. Yours.
                </span>
            </p>
            <div
                style={{
                    display: "flex",
                    gap: "0.75rem",
                    flexWrap: "wrap",
                    justifyContent: "center",
                }}
            >
                <button
                    type="button"
                    onClick={onEnter}
                    style={{
                        background: C.accent,
                        color: "#fff",
                        border: "none",
                        padding: "0.9rem 1.75rem",
                        fontFamily: C.font,
                        fontSize: "0.95rem",
                        cursor: "pointer",
                        borderRadius: "0.25rem",
                        boxShadow: `0 0 28px ${C.accentGlow}`,
                    }}
                >
                    Create your vault — 60 seconds
                </button>
                <button
                    type="button"
                    onClick={onEnter}
                    style={{
                        background: "transparent",
                        color: C.text,
                        border: `1px solid ${C.borderStrong}`,
                        padding: "0.9rem 1.75rem",
                        fontFamily: C.font,
                        fontSize: "0.95rem",
                        cursor: "pointer",
                        borderRadius: "0.25rem",
                    }}
                >
                    I already have one
                </button>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/*  3-Pillar block                                                    */
/* ------------------------------------------------------------------ */
function Pillars() {
    const items = [
        {
            icon: "🔒",
            title: "Truly anonymous",
            body: "No signup. Your recovery phrase is your identity. We don't know you exist — and we can't, even if we wanted to.",
        },
        {
            icon: "📂",
            title: "For everything sensitive",
            body: "Crypto seed phrases. Passport scans. 2FA codes. Bank details. Wills. Ten purpose-built categories — not just a list of usernames.",
        },
        {
            icon: "🪙",
            title: "Pay in crypto, or not at all",
            body: "Free forever for one device. Upgrade with BTC, ETH, USDC or Monero — no card, no name, no receipt in your inbox.",
        },
    ];
    return (
        <Section>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "1.25rem",
                }}
            >
                {items.map((p) => (
                    <div
                        key={p.title}
                        style={{
                            padding: "1.5rem",
                            border: `1px solid ${C.border}`,
                            borderRadius: "0.4rem",
                            background: C.surface,
                            fontFamily: C.font,
                        }}
                    >
                        <div style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>{p.icon}</div>
                        <div style={{ color: C.text, fontWeight: 500, marginBottom: "0.5rem" }}>
                            {p.title}
                        </div>
                        <div style={{ color: C.muted, fontSize: "0.88rem", lineHeight: 1.6 }}>
                            {p.body}
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  How it works                                                      */
/* ------------------------------------------------------------------ */
function HowItWorks() {
    const steps = [
        {
            n: "01",
            title: "You get a 12-word recovery phrase",
            body: "Generated on your device. We never see it. This is your only login — no email, no password, no second factor required to start. Write it down. Print it. Tuck the card in a safe.",
        },
        {
            n: "02",
            title: "Your device derives the keys",
            body: "From that phrase, your browser computes two things, locally: a vault ID (a one-way hash — we use it to find your encrypted blob) and a master key (used to encrypt your data). The keys never leave your device.",
        },
        {
            n: "03",
            title: "Only ciphertext touches our servers",
            body: "Every entry is encrypted with AES-GCM before it ever leaves your browser. We store the encrypted blob keyed by your vault ID. To us it looks like random noise. If our database leaked tomorrow, attackers would get nothing usable.",
        },
    ];
    return (
        <Section
            eyebrow="HOW IT WORKS"
            title="Three steps. Zero ways for us to read your stuff."
            subtitle="Hikari Vault is built like a crypto wallet — your phrase is your identity, and all the encryption happens on your device before anything is sent over the wire."
            background={C.surface}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {steps.map((s) => (
                    <div
                        key={s.n}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "auto 1fr",
                            gap: "1.5rem",
                            padding: "1.5rem",
                            border: `1px solid ${C.border}`,
                            borderRadius: "0.4rem",
                            background: C.bg,
                            fontFamily: C.font,
                        }}
                    >
                        <div style={{ color: C.accent, fontSize: "1.5rem", fontWeight: 500 }}>
                            {s.n}
                        </div>
                        <div>
                            <div style={{ color: C.text, fontWeight: 500, marginBottom: "0.4rem" }}>
                                {s.title}
                            </div>
                            <div style={{ color: C.muted, fontSize: "0.9rem", lineHeight: 1.6 }}>
                                {s.body}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Where your data lives                                             */
/* ------------------------------------------------------------------ */
function WhereData() {
    return (
        <Section
            eyebrow="WHERE YOUR DATA LIVES"
            title="On your device, encrypted. In our cloud, encrypted. We hold the lock — only you hold the key."
            subtitle="A common question: 'okay but where is my stuff actually stored?' Honest answer below."
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "1rem",
                    fontFamily: C.font,
                }}
            >
                {[
                    {
                        tag: "ON YOUR DEVICE",
                        title: "Browser local storage",
                        body: "Your encrypted vault is cached in your browser's localStorage. This means it loads instantly, works offline, and is yours — even if our servers vanished tomorrow you could still decrypt your data with your recovery phrase and any backup file.",
                    },
                    {
                        tag: "IN OUR CLOUD",
                        title: "Hikari Vault sync server",
                        body: "An encrypted copy syncs to our backend so you can unlock on a second device. We store only ciphertext, keyed by your vault ID (a hash). We don't know your name, email, IP, or what's inside. You can also self-host this server — full source available.",
                    },
                    {
                        tag: "ON YOUR DEVICE ONLY",
                        title: "The decryption key",
                        body: "The master key that turns ciphertext into readable entries is derived from your recovery phrase, on your device, every time you unlock. It is never transmitted, never stored on our servers, and never reaches our logs. Lose the phrase and we cannot help — that's the price of zero-knowledge.",
                    },
                ].map((b) => (
                    <div
                        key={b.title}
                        style={{
                            padding: "1.5rem",
                            border: `1px solid ${C.border}`,
                            borderRadius: "0.4rem",
                            background: C.surface,
                        }}
                    >
                        <div
                            style={{
                                fontSize: "0.65rem",
                                letterSpacing: "0.3em",
                                color: C.accent,
                                marginBottom: "0.6rem",
                            }}
                        >
                            {b.tag}
                        </div>
                        <div style={{ color: C.text, fontWeight: 500, marginBottom: "0.5rem" }}>
                            {b.title}
                        </div>
                        <div style={{ color: C.muted, fontSize: "0.88rem", lineHeight: 1.6 }}>
                            {b.body}
                        </div>
                    </div>
                ))}
            </div>
            <div
                style={{
                    marginTop: "1.5rem",
                    padding: "1rem 1.25rem",
                    border: `1px solid ${C.border}`,
                    borderRadius: "0.4rem",
                    background: C.bg,
                    color: C.muted,
                    fontSize: "0.82rem",
                    lineHeight: 1.65,
                    fontFamily: C.font,
                }}
            >
                <span style={{ color: C.warn }}>★</span>&nbsp;
                <strong style={{ color: C.text }}>Translation:</strong> if a court served us a warrant
                tomorrow for your data, we would hand over an encrypted blob and a hash. There is no
                back door we could be compelled to use. We designed it that way on purpose.
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  What goes in your vault (10 categories)                           */
/* ------------------------------------------------------------------ */
function Categories() {
    const cats = [
        { icon: "🪙", name: "Crypto", body: "Seed phrases, hardware wallet PINs, exchange API keys. BIP-39 validation built in." },
        { icon: "🪪", name: "Identity", body: "Passport, driver's licence, SSN, birth certificate. Optional photo upload." },
        { icon: "💳", name: "Finance", body: "Bank accounts, IBAN, SWIFT, card numbers, savings logins." },
        { icon: "🔁", name: "Recovery codes", body: "2FA backup codes for every service. Stored as scannable chips." },
        { icon: "👨\u200d👩\u200d👧", name: "Family", body: "Wi-Fi password, alarm code, kids' school logins, parents' meds." },
        { icon: "⚖️", name: "Legal", body: "Wills, POAs, leases, contracts. Encrypted attachment storage on Plus." },
        { icon: "🩺", name: "Health", body: "Blood type, allergies, vaccination records, prescriptions." },
        { icon: "⚙️", name: "Work", body: "API keys, .env secrets, server SSH, client credentials." },
        { icon: "📝", name: "Notes", body: "Freeform encrypted notes for anything that doesn't fit a template." },
        { icon: "📎", name: "Files", body: "Arbitrary encrypted attachments — PDFs, images, JSON. Plus tier." },
    ];
    return (
        <Section
            eyebrow="WHAT GOES IN YOUR VAULT"
            title="Ten purpose-built homes for the things you can't afford to lose."
            subtitle="Each category has its own form — not just key/value pairs. Crypto entries validate BIP-39 wordlists. Identity entries accept photos. Recovery codes render as one-tap chips. The right shape for the right secret."
            background={C.surface}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "0.85rem",
                    fontFamily: C.font,
                }}
            >
                {cats.map((c) => (
                    <div
                        key={c.name}
                        style={{
                            padding: "1.1rem",
                            border: `1px solid ${C.border}`,
                            borderRadius: "0.4rem",
                            background: C.bg,
                        }}
                    >
                        <div style={{ fontSize: "1.2rem", marginBottom: "0.45rem" }}>{c.icon}</div>
                        <div style={{ color: C.text, fontWeight: 500, marginBottom: "0.35rem", fontSize: "0.92rem" }}>
                            {c.name}
                        </div>
                        <div style={{ color: C.muted, fontSize: "0.78rem", lineHeight: 1.55 }}>{c.body}</div>
                    </div>
                ))}
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  "What if I can't get in?" — recovery story                        */
/* ------------------------------------------------------------------ */
function Recovery({ onNav }) {
    return (
        <Section
            eyebrow="WHAT IF I CAN'T GET IN?"
            title="Five separate ways back into your vault. You only need one to survive."
            subtitle="Zero-knowledge security means we can never help you recover by 'verifying your identity' — because we don't have one for you. So we built layered recovery on your side instead."
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "1rem",
                    fontFamily: C.font,
                }}
            >
                {[
                    {
                        n: "1",
                        title: "Your 12-word recovery phrase",
                        body: "The primary key to your vault. Shown once, at setup. We force you to type back 3 random words before you can exit setup — so you can't accidentally skip writing it down.",
                    },
                    {
                        n: "2",
                        title: "Auto-downloaded encrypted backup file",
                        body: "At setup, your browser auto-downloads a `.hvault` file — your full encrypted vault as a single document. Keep it on a USB, in iCloud, in email-to-self. Decryptable only with your phrase.",
                    },
                    {
                        n: "3",
                        title: "Printable recovery card (PDF)",
                        body: "Generated on-device. Includes your phrase, your Shamir share if you set one up, and one-page emergency instructions for whoever finds it. Put it in a safe, a deposit box, with a lawyer.",
                    },
                    {
                        n: "4",
                        title: "Shamir secret sharing (Plus, optional)",
                        body: "Split your phrase across 3–5 trusted people. Any 2 (or 3) of them can reconstruct it for you. Nobody can do it alone. You stay in control while building a safety net.",
                    },
                    {
                        n: "5",
                        title: "PIN or biometric on enrolled devices",
                        body: "On any device where you've unlocked at least once, your PIN or Face ID/Touch ID re-opens the vault. So 'I forgot my phrase' on a working device still gets you in.",
                    },
                ].map((r) => (
                    <div
                        key={r.n}
                        style={{
                            padding: "1.5rem",
                            border: `1px solid ${C.border}`,
                            borderRadius: "0.4rem",
                            background: C.surface,
                        }}
                    >
                        <div
                            style={{
                                color: C.accent,
                                fontSize: "1.5rem",
                                fontWeight: 500,
                                marginBottom: "0.5rem",
                            }}
                        >
                            {r.n}
                        </div>
                        <div style={{ color: C.text, fontWeight: 500, marginBottom: "0.5rem" }}>
                            {r.title}
                        </div>
                        <div style={{ color: C.muted, fontSize: "0.88rem", lineHeight: 1.6 }}>
                            {r.body}
                        </div>
                    </div>
                ))}
            </div>
            <div
                style={{
                    marginTop: "1.75rem",
                    padding: "1.1rem 1.25rem",
                    border: `1px solid ${C.danger}40`,
                    borderRadius: "0.4rem",
                    background: "rgba(217,43,43,0.06)",
                    fontFamily: C.font,
                    lineHeight: 1.65,
                }}
            >
                <div style={{ color: C.danger, fontSize: "0.72rem", letterSpacing: "0.3em", marginBottom: "0.4rem" }}>
                    THE HONEST DISCLAIMER
                </div>
                <div style={{ color: C.text, fontSize: "0.9rem" }}>
                    If you lose <em>all five</em> — your phrase, your backup file, your printed card,
                    your Shamir shares, <em>and</em> every device you ever unlocked on — your data is
                    permanently unrecoverable. That is the price of true zero-knowledge encryption.
                    We cannot help. We physically cannot. Read{" "}
                    <button
                        type="button"
                        onClick={() => onNav("security")}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: C.accent,
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontFamily: C.font,
                            fontSize: "0.9rem",
                            padding: 0,
                        }}
                    >
                        the security model
                    </button>{" "}
                    to understand exactly why.
                </div>
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Pricing                                                           */
/* ------------------------------------------------------------------ */
function Pricing({ onEnter }) {
    const tiers = [
        {
            name: "Free",
            price: "$0",
            blurb: "Forever. Just here to use the thing.",
            features: [
                "Unlimited entries",
                "1 device",
                "All 10 entry categories",
                "Recovery phrase + auto backup",
                "Printable recovery card",
                "Open-source self-host option",
            ],
            cta: "Create your vault",
            highlight: false,
        },
        {
            name: "Plus",
            price: "$4/mo",
            altPrice: "or $40/year · $120 lifetime",
            blurb: "For the things you really can't lose.",
            features: [
                "Everything in Free",
                "Multi-device sync",
                "Encrypted file attachments (PDFs, images)",
                "Shamir trustee setup (up to 5 trustees)",
                "Priority recovery support",
                "Inheritance dead-man's switch (coming v2)",
            ],
            cta: "Upgrade with crypto",
            highlight: true,
        },
    ];
    return (
        <Section
            id="pricing"
            eyebrow="PRICING"
            title="Pay in crypto, or don't pay at all."
            subtitle="No cards. No subscriptions you forgot to cancel. No email receipts. BTC · ETH · USDC · Monero accepted. Crypto checkout coming with M3."
            background={C.surface}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "1rem",
                    fontFamily: C.font,
                }}
            >
                {tiers.map((t) => (
                    <div
                        key={t.name}
                        style={{
                            padding: "1.75rem",
                            border: `1px solid ${t.highlight ? C.accent : C.border}`,
                            borderRadius: "0.4rem",
                            background: C.bg,
                            boxShadow: t.highlight ? `0 0 32px ${C.accentGlow}` : "none",
                        }}
                    >
                        <div
                            style={{
                                color: t.highlight ? C.accent : C.muted,
                                fontSize: "0.72rem",
                                letterSpacing: "0.3em",
                                marginBottom: "0.75rem",
                            }}
                        >
                            {t.name.toUpperCase()}
                        </div>
                        <div style={{ display: "baseline", marginBottom: "0.25rem" }}>
                            <span
                                style={{
                                    color: C.text,
                                    fontSize: "2.2rem",
                                    fontWeight: 500,
                                }}
                            >
                                {t.price}
                            </span>
                        </div>
                        {t.altPrice && (
                            <div style={{ color: C.dim, fontSize: "0.78rem", marginBottom: "0.5rem" }}>
                                {t.altPrice}
                            </div>
                        )}
                        <div style={{ color: C.muted, fontSize: "0.88rem", marginBottom: "1.25rem" }}>
                            {t.blurb}
                        </div>
                        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.5rem 0" }}>
                            {t.features.map((f) => (
                                <li
                                    key={f}
                                    style={{
                                        color: C.text,
                                        fontSize: "0.88rem",
                                        padding: "0.35rem 0",
                                        display: "flex",
                                        gap: "0.5rem",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <span style={{ color: C.accent }}>✓</span>
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            onClick={onEnter}
                            style={{
                                width: "100%",
                                background: t.highlight ? C.accent : "transparent",
                                color: t.highlight ? "#fff" : C.text,
                                border: t.highlight ? "none" : `1px solid ${C.borderStrong}`,
                                padding: "0.85rem 1rem",
                                fontFamily: C.font,
                                fontSize: "0.9rem",
                                cursor: "pointer",
                                borderRadius: "0.25rem",
                            }}
                        >
                            {t.cta}
                        </button>
                    </div>
                ))}
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Nonprofit programme                                               */
/* ------------------------------------------------------------------ */
function Nonprofit() {
    return (
        <Section
            id="nonprofit"
            eyebrow="NONPROFIT PROGRAMME"
            title="Free Plus for journalists, activists, and NGOs working under pressure."
            subtitle="If you're working in human rights, press freedom, dissident protection, humanitarian aid, or any context where being identified would put you in danger — we'd like Hikari Vault to be useful to you, free."
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "1rem",
                    fontFamily: C.font,
                }}
            >
                {[
                    {
                        title: "What we provide",
                        body: "Full Plus tier — multi-device sync, file attachments, Shamir trustees, priority support. Indefinitely, no renewal nags.",
                    },
                    {
                        title: "How to apply",
                        body: "A reference from a recognised partner organisation (EFF, Access Now, RSF, Tor Project, Internews — or similar) is enough. We don't ask for real names, government ID, or organisational paperwork.",
                    },
                    {
                        title: "What we don't do",
                        body: "We don't publish a list of nonprofit users. We don't track your usage. We don't ping you for testimonials. You're a user like any other, with the bill on us.",
                    },
                ].map((b) => (
                    <div
                        key={b.title}
                        style={{
                            padding: "1.5rem",
                            border: `1px solid ${C.border}`,
                            borderRadius: "0.4rem",
                            background: C.surface,
                        }}
                    >
                        <div style={{ color: C.text, fontWeight: 500, marginBottom: "0.5rem" }}>
                            {b.title}
                        </div>
                        <div style={{ color: C.muted, fontSize: "0.88rem", lineHeight: 1.6 }}>
                            {b.body}
                        </div>
                    </div>
                ))}
            </div>
            <div
                style={{
                    marginTop: "1.5rem",
                    fontFamily: C.font,
                    color: C.muted,
                    fontSize: "0.85rem",
                }}
            >
                Application form opens with v1 launch. Until then, you can use the free tier without
                limits if you're operating with a single device.
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  FAQ                                                               */
/* ------------------------------------------------------------------ */
function FAQItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div
            style={{
                borderBottom: `1px solid ${C.border}`,
                fontFamily: C.font,
            }}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    color: C.text,
                    padding: "1.1rem 0",
                    textAlign: "left",
                    fontFamily: C.font,
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "1rem",
                    alignItems: "center",
                }}
            >
                <span>{q}</span>
                <span style={{ color: C.accent, fontSize: "1.2rem" }}>
                    {open ? "−" : "+"}
                </span>
            </button>
            {open && (
                <div
                    style={{
                        color: C.muted,
                        fontSize: "0.9rem",
                        lineHeight: 1.7,
                        paddingBottom: "1.25rem",
                        paddingRight: "2rem",
                    }}
                >
                    {a}
                </div>
            )}
        </div>
    );
}

function FAQ() {
    const items = [
        {
            q: "What happens if I lose my recovery phrase?",
            a: "We can't help you. That's not a slogan — it's the architecture. We never see your phrase, we don't store any reset mechanism, and there is no 'contact support' path that bypasses it. That's why setup forces you to verify your phrase, why we auto-download an encrypted backup file, why we offer a printable recovery card, and why Shamir trustees exist. Use at least two of those mechanisms and you'll be fine.",
        },
        {
            q: "Where exactly is my data stored?",
            a: "Two places. (1) On your device, in your browser's localStorage — encrypted. (2) On Hikari Vault's sync server, in a MongoDB database — also encrypted. The decryption key never leaves your device. We see ciphertext indexed by a hash. You can also self-host the sync server with one Docker command (open-source release coming).",
        },
        {
            q: "Can you read my entries?",
            a: "No. Every entry is encrypted with AES-256-GCM on your device using a key derived from your recovery phrase. Our server only sees the output of that encryption. We never see the inputs. If a government compelled us to hand over your data, we'd hand over a base64 blob and a hash — which is useless without your phrase.",
        },
        {
            q: "Why crypto-only payments?",
            a: "Because credit cards leak metadata — your name, address, transaction history — to Visa, your bank, and us. Stripe sends you a receipt to an email we'd then have on file. Crypto payments (especially via self-hosted BTCPay) collect none of that. We get an on-chain transaction; we issue you an anonymous licence code. Neither party knows the other's identity.",
        },
        {
            q: "Is this open source?",
            a: "The backend will be MIT-licensed and on GitHub by M5 (~4–5 weeks). The frontend follows shortly after. Self-host instructions ship at the same time.",
        },
        {
            q: "Is it independently audited?",
            a: "Not yet. We'll commission an audit (Trail of Bits, Cure53, or Radically Open Security) after the v1 code freeze and publish the full report — findings, remediations, and follow-up. Until then, treat us as 'plausible but unproven' and use accordingly.",
        },
        {
            q: "Does it autofill passwords in my browser?",
            a: "No. Hikari Vault is deliberately not a browser-autofill password manager — 1Password, Bitwarden, and Apple Passwords do that well. We compete with your iPhone Notes app and your screenshots folder, not with autofill tools. Use both: a password manager for logins, Hikari Vault for the things you'd never put in a password manager.",
        },
        {
            q: "Can I share a vault with my partner or family?",
            a: "Not in v1. Today, both people would unlock the same vault using the same phrase. Real multi-user sharing (separate identities, granular permissions) is on the v3 roadmap.",
        },
        {
            q: "Is there a mobile app?",
            a: "Not yet — Hikari Vault runs as a web app today. It works in any modern mobile browser and supports Face ID / Touch ID via WebAuthn. Native iOS / Android apps are on the v4 roadmap.",
        },
        {
            q: "I lost a device that had my vault unlocked. What do I do?",
            a: "If you used biometric unlock on that device, revoke the biometric credential in Settings on any other device where you're still logged in. The device-specific wrapped key on the lost phone becomes useless without it. Your master key is never stored on a server, so even an unlocked device can't be cloned remotely.",
        },
        {
            q: "What does Hikari Brand actually do?",
            a: "Hikari Brand operates a portfolio of independent products. Hikari Vault is the Security & Privacy division's first release. Other products are kept separate by design — no shared accounts, no cross-product analytics, no surprises.",
        },
        {
            q: "How do I delete my vault?",
            a: "Open Settings → Wipe vault. It clears all encrypted data from your device and deletes the encrypted blob from our server. If you're a Plus user, your licence code remains usable for the remainder of its term on a future vault. No 'are you sure' email follow-up — we can't send one, we don't have your email.",
        },
    ];
    return (
        <Section
            id="faq"
            eyebrow="FREQUENTLY ASKED"
            title="The questions everyone asks before trusting a vault."
            background={C.surface}
        >
            <div style={{ maxWidth: "780px" }}>
                {items.map((it) => (
                    <FAQItem key={it.q} q={it.q} a={it.a} />
                ))}
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Closing CTA                                                       */
/* ------------------------------------------------------------------ */
function ClosingCTA({ onEnter }) {
    return (
        <section
            style={{
                padding: "6rem 1.5rem",
                textAlign: "center",
                background:
                    "radial-gradient(ellipse 60% 60% at 50% 100%, rgba(11,126,163,0.18), transparent 70%)",
            }}
        >
            <h2
                style={{
                    fontSize: "clamp(1.6rem, 4vw, 2.5rem)",
                    fontWeight: 500,
                    margin: "0 auto 1rem",
                    maxWidth: "22ch",
                    lineHeight: 1.15,
                    fontFamily: C.font,
                    color: C.text,
                }}
            >
                Sixty seconds. No email. No second thoughts.
            </h2>
            <p
                style={{
                    color: C.muted,
                    maxWidth: "44ch",
                    margin: "0 auto 2rem",
                    lineHeight: 1.6,
                    fontFamily: C.font,
                    fontSize: "0.95rem",
                }}
            >
                Hikari Vault is free forever for one device. Start with one thing you've been
                meaning to move out of Notes.
            </p>
            <button
                type="button"
                onClick={onEnter}
                style={{
                    background: C.accent,
                    color: "#fff",
                    border: "none",
                    padding: "0.9rem 2rem",
                    fontFamily: C.font,
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    borderRadius: "0.25rem",
                    boxShadow: `0 0 28px ${C.accentGlow}`,
                }}
            >
                Create your vault
            </button>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/*  Landing — page composition                                        */
/* ------------------------------------------------------------------ */
export default function Landing({ onEnter, onNav }) {
    return (
        <main>
            <Hero onEnter={onEnter} />
            <Pillars />
            <HowItWorks />
            <WhereData />
            <Categories />
            <Recovery onNav={onNav} />
            <Pricing onEnter={onEnter} />
            <Nonprofit />
            <FAQ />
            <ClosingCTA onEnter={onEnter} />
        </main>
    );
}
